import promocionesService from "../../services/administrar/promociones.js";

const RESUMEN_VACIO = {
  promociones_activas: 0,
  promociones_por_vencer: 0,
  promociones_vencidas: 0,
  promociones_inactivas: 0,
  promociones_sin_cuentas: 0,
  promociones_total: 0,
  cuentas_activas: 0,
  cuentas_por_vencer: 0,
  cuentas_vencidas: 0,
  cuentas_inactivas: 0,
  cuentas_total: 0,
};

export default {
  namespaced: true,
  state: {
    promociones: [],
    meta: null,
    resumen: { ...RESUMEN_VACIO },

    // filtros
    cuotaFiltro: "todas", // "todas" | "sin_interes" | "con_interes"
    clienteId: 0,
    cuentaId: 0,

    // filtros nuevos (buscador + estado agregado)
    estado: "", // "" | "activa" | "por_vencer" | "vencida" | "inactiva" | "sin_cuentas"
    search: "", // caja única: nombre promoción + cuenta + cliente + bin
    clienteNombre: "",
    cuentaNombre: "",
    bin: "",

    // paginación
    page: 1,
    rowsPerPage: 10,
    total: 0,

    loading: false,
  },

  mutations: {
    setPromociones(state, value) {
      state.promociones = value;
    },
    setMeta(state, value) {
      state.meta = value;
    },
    setResumen(state, value) {
      state.resumen = value ? { ...RESUMEN_VACIO, ...value } : { ...RESUMEN_VACIO };
    },
    setCuotaFiltro(state, value) {
      state.cuotaFiltro = value;
    },
    setClienteId(state, value) {
      state.clienteId = value;
    },
    setCuentaId(state, value) {
      state.cuentaId = value;
    },
    setEstado(state, value) {
      state.estado = value || "";
    },
    setSearch(state, value) {
      state.search = value || "";
    },
    setClienteNombre(state, value) {
      state.clienteNombre = value || "";
    },
    setCuentaNombre(state, value) {
      state.cuentaNombre = value || "";
    },
    setBin(state, value) {
      state.bin = value || "";
    },
    setPage(state, value) {
      state.page = value;
    },
    setRowsPerPage(state, value) {
      state.rowsPerPage = value;
    },
    setTotal(state, value) {
      state.total = value;
    },
    setLoading(state, value) {
      state.loading = value;
    },
    updateCuentaActivo(state, { promocionId, cuentaPromocionId, activo }) {
      const promo = state.promociones.find((p) => p.id === promocionId);
      if (!promo || !Array.isArray(promo.cuentas)) return;
      const idx = promo.cuentas.findIndex(
        (c) => c.cuenta_promocion_id === cuentaPromocionId,
      );
      if (idx === -1) return;
      promo.cuentas.splice(idx, 1, { ...promo.cuentas[idx], is_active: activo });
    },
    removePromocion(state, id) {
      state.promociones = state.promociones.filter((p) => p.id !== id);
    },
    removeCuentaPromocion(state, { promocionId, cuentaPromocionId }) {
      const promo = state.promociones.find((p) => p.id === promocionId);
      if (!promo || !Array.isArray(promo.cuentas)) return;
      promo.cuentas = promo.cuentas.filter(
        (c) => c.cuenta_promocion_id !== cuentaPromocionId,
      );
    },
  },

  actions: {
    async getPromociones({ commit, state }) {
      commit("setLoading", true);
      try {
        const params = {
          Number: state.page,
          Size: state.rowsPerPage,
        };
        if (state.cuotaFiltro === "sin_interes") params.CuotaSinInteres = true;
        if (state.cuotaFiltro === "con_interes") params.CuotaConInteres = true;
        if (state.cuentaId > 0) params.CuentasID = state.cuentaId;

        // Filtros nuevos — se mandan con el nombre snake_case que espera el
        // backend (tag `query:"..."` en pkg/filtros/administracion/promocion.go).
        if (state.estado) params.estado = state.estado;
        if (state.search) params.search = state.search;
        if (state.clienteNombre) params.cliente_nombre = state.clienteNombre;
        if (state.cuentaNombre) params.cuenta_nombre = state.cuentaNombre;
        if (state.bin) params.bin = state.bin;

        const resp = await promocionesService.getPromociones(params);
        const data = resp?.data || {};
        commit("setPromociones", data.promociones || []);
        commit("setMeta", data.meta || null);
        commit("setResumen", data.resumen);
        // paginación del backend: data.meta.page.{total|lastPage|currentPage|...}
        const totalRegistros = data.meta?.page?.total ?? (data.promociones || []).length;
        commit("setTotal", totalRegistros);
      } catch (error) {
        console.error(error);
        commit("setPromociones", []);
        commit("setResumen", null);
        commit("setTotal", 0);
      } finally {
        commit("setLoading", false);
      }
    },

    async deletePromocion({ commit, dispatch }, id) {
      await promocionesService.deletePromocion(id);
      commit("removePromocion", id);
      // refrescar para que la paginación quede consistente con el backend
      dispatch("getPromociones");
    },

    async toggleActivarCuenta({ commit }, { promocionId, cuentaPromocionId, activo }) {
      const resp = await promocionesService.actDesactPromocion(cuentaPromocionId, activo);
      commit("updateCuentaActivo", { promocionId, cuentaPromocionId, activo });
      return resp;
    },

    async deleteCuentaPromocion({ commit }, { promocionId, cuentaPromocionId }) {
      await promocionesService.deleteCuentaPromocion(cuentaPromocionId);
      commit("removeCuentaPromocion", { promocionId, cuentaPromocionId });
    },

    setPage({ commit, dispatch }, page) {
      commit("setPage", page);
      dispatch("getPromociones");
    },

    setRowsPerPage({ commit, dispatch }, size) {
      commit("setRowsPerPage", size);
      commit("setPage", 1);
      dispatch("getPromociones");
    },

    applyFiltros({ commit, dispatch }, filtros) {
      if ("cuotaFiltro" in filtros) commit("setCuotaFiltro", filtros.cuotaFiltro || "todas");
      if ("clienteId" in filtros) commit("setClienteId", filtros.clienteId || 0);
      if ("cuentaId" in filtros) commit("setCuentaId", filtros.cuentaId || 0);
      if ("estado" in filtros) commit("setEstado", filtros.estado);
      if ("search" in filtros) commit("setSearch", filtros.search);
      if ("clienteNombre" in filtros) commit("setClienteNombre", filtros.clienteNombre);
      if ("cuentaNombre" in filtros) commit("setCuentaNombre", filtros.cuentaNombre);
      if ("bin" in filtros) commit("setBin", filtros.bin);
      commit("setPage", 1);
      dispatch("getPromociones");
    },
  },

  getters: {
    promociones: (state) => state.promociones,
    loading: (state) => state.loading,
    total: (state) => state.total,
    page: (state) => state.page,
    rowsPerPage: (state) => state.rowsPerPage,
    cuotaFiltro: (state) => state.cuotaFiltro,
    clienteId: (state) => state.clienteId,
    cuentaId: (state) => state.cuentaId,
    estado: (state) => state.estado,
    search: (state) => state.search,
    clienteNombre: (state) => state.clienteNombre,
    cuentaNombre: (state) => state.cuentaNombre,
    bin: (state) => state.bin,
    resumen: (state) => state.resumen,
  },
};
